import {
  Avatar,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { translateKey } from 'src/locales/translate';
import { format } from 'date-fns';
import { ReactNode, useState } from 'react';
import OrganizationLink from 'src/components/organization-link/OrganizationLink';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom } from 'src/components/table';
import VisitStatusBadge from 'src/components/visit-status-badge/VisitStatusBadge';
import {
  GetVisitById,
  GetVisitByIdRejectedByEnum,
  GetVisitByIdStatusEnum,
} from 'woofwoof-api';
import Image from '../../../../components/image';
import OrganizationVisitModal from '../../organization/components/OrganizationVisitModal/OrganizationVisitModal';
import { useOrganizationContext } from '../../organization/hooks/useOrganizationContext';
import VisitModal from './VisitModal';
import {
  TAB_TABLE_CONTAINER_SX,
  TAB_TABLE_PAGINATION_SX,
  TAB_TABLE_ROWS_PER_PAGE_OPTIONS,
  TAB_TABLE_SCROLL_BODY_SX,
} from 'src/theme/tableContainer';
import VisitNotesStatusBadge, {
  resolveVisitNotesStatus,
  VisitNotesStatus,
} from 'src/components/visit-status-badge/VisitNotesStatusBadge';
import hasRichTextContent from 'src/utils/hasRichTextContent';
import { formatVisitClosure } from 'src/utils/visitClosure';

interface Row {
  id: string;
  professional: string;
  professionalAvatar?: string;
  date: string;
  closedAt?: string | null;
  endTime?: string;
  organizationName?: string;
  organizationSlug?: string;
  organizationId?: string;
  status: GetVisitByIdStatusEnum;
  rejectedBy?: GetVisitByIdRejectedByEnum;
  notesStatus: VisitNotesStatus | null;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left';
  render?: (row: Row) => ReactNode;
}

export type ProfileVisitsHistoryTableProps = {
  data: GetVisitById[];
  /**
   * Paging is driven from above. The Historia tab pages on the server; the
   * owner-facing view holds every visit and pages in the browser. Either way
   * `data` is the rows to draw and `total` is how many exist.
   */
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * What an empty table should say. A filtered history that matches nothing is
   * not the same as a patient with no visits, and telling the professional the
   * latter would be a lie they might act on.
   */
  emptyMessage?: string;
};

export default function ProfileVisitsHistoryTable({
  data,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  emptyMessage,
}: ProfileVisitsHistoryTableProps) {
  const theme = useTheme();
  const { isProfessionalRoleSelected, isOrganizationAdminRoleSelected } =
    useOrganizationContext();
  const [selectedVisitId, setSelectedVisitId] = useState<string>('');

  const openModal = (visitId: string) => setSelectedVisitId(visitId);

  const closeModal = () => setSelectedVisitId('');

  const handleOnRowClick = (visitId: string) => {
    openModal(visitId);
  };

  const tableData: Row[] = data.map((visit) => ({
    id: visit.id,
    professional: `${visit?.professional?.firstName} ${visit?.professional?.lastName}`,
    professionalAvatar: visit?.professional?.avatar,
    date: visit?.startTime,
    closedAt: visit?.closedAt,
    endTime: visit?.endTime,
    organizationName: visit?.organization?.name,
    organizationSlug: visit?.organization?.slug,
    organizationId: visit?.organization?.id,
    status: visit?.status,
    rejectedBy: visit?.rejectedBy,
    // Resolved the same way as on Historia wizyt, so a row reads the same
    // wherever it appears — and so the Notatka filter and this column cannot
    // disagree about it.
    notesStatus: resolveVisitNotesStatus({
      status: visit?.status,
      hasNotes: hasRichTextContent(visit?.notes),
      hasNotesDraft: visit?.hasNotesDraft,
    }),
  }));

  const COLUMNS: Column[] = [
    {
      id: 'professional',
      label: translateKey('search.book.cardSpecialist'),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={value.professionalAvatar}
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />
          <Stack>
            <Typography variant="body2">{value.professional}</Typography>
            <OrganizationLink
              name={value.organizationName}
              slug={value.organizationSlug}
              id={value.organizationId}
              color="text.secondary"
              variant="caption"
            />
          </Stack>
        </Box>
      ),
    },
    {
      id: 'date',
      label: translateKey('visit.tableDate'),
      render: (value: Row) => format(new Date(value.date), 'dd/MM/yyyy H:mm'),
    },
    {
      id: 'closedAt',
      label: translateKey('visit.historyClosedColumn'),
      render: (value: Row) => formatVisitClosure(value),
    },
    {
      id: 'notesStatus',
      label: translateKey('visit.notesStatusColumn'),
      render: (value: Row) =>
        value.notesStatus ? (
          <VisitNotesStatusBadge status={value.notesStatus} />
        ) : null,
    },
    {
      id: 'status',
      label: translateKey('visit.tableStatus'),
      render: (value: Row) => (
        <VisitStatusBadge
          visitStatus={value.status}
          rejectedBy={value.rejectedBy}
        />
      ),
    },
  ];

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={TAB_TABLE_SCROLL_BODY_SX}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row, rowIndex) => (
              <TableRow
                hover
                key={rowIndex}
                data-testid="patient-history-row"
                data-visit-id={row.id}
                onClick={() => handleOnRowClick(row.id)}
              >
                {COLUMNS.map((column, index) => {
                  const value = row[column.id];
                  return (
                    <TableCell key={column.id} align={column.align}>
                      {column.render ? column.render(row) : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );
  return (
    <>
      <TableContainer sx={TAB_TABLE_CONTAINER_SX}>
        {tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty waiting room"
                src="/assets/images/empty_table.svg"
              />
              <Typography>
                {emptyMessage ?? translateKey('visit.upcomingNoVisits')}
              </Typography>
            </Stack>
          </>
        )}
      </TableContainer>
      <TablePaginationCustom
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={TAB_TABLE_ROWS_PER_PAGE_OPTIONS}
        sx={TAB_TABLE_PAGINATION_SX}
      />
      {isOrganizationAdminRoleSelected || isProfessionalRoleSelected ? (
        <OrganizationVisitModal
          isOpen={Boolean(selectedVisitId)}
          onClose={closeModal}
          visit={data.find((visit) => visit.id === selectedVisitId)}
        />
      ) : (
        <VisitModal
          isOpen={Boolean(selectedVisitId)}
          onClose={closeModal}
          visit={data.find((visit) => visit.id === selectedVisitId)}
        />
      )}
    </>
  );
}
